import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

class NotificationService {
  constructor() {
    this.sentEmailsRef = collection(db, 'sentEmails');
    this.notificationsRef = collection(db, 'notifications');
    this.settingsRef = collection(db, 'notificationSettings');
  }

  async getSettings(userId) {
    try {
      const { getDoc, doc } = await import('firebase/firestore');
      const docRef = doc(db, 'notificationSettings', userId);
      const docSnap = await getDoc(docRef);
      return {
        success: true,
        settings: docSnap.exists() ? docSnap.data() : this.getDefaultSettings()
      };
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      return { success: false, error: error.message };
    }
  }

  async updateSettings(userId, settings) {
    try {
      const { setDoc, doc } = await import('firebase/firestore');
      const docRef = doc(db, 'notificationSettings', userId);
      await setDoc(docRef, settings, { merge: true });
      return { success: true };
    } catch (error) {
      console.error('Error updating notification settings:', error);
      return { success: false, error: error.message };
    }
  }

  async sendTestEmail(userId, email) {
    try {
      const sendTestEmail = httpsCallable(functions, 'sendTestNotification');
      const result = await sendTestEmail({ userId, email });
      return { success: true, result: result.data };
    } catch (error) {
      console.error('Error sending test email:', error);
      return { success: false, error: error.message };
    }
  }

  async verifyEmailDelivery(notificationId) {
    try {
      const { getDoc, doc } = await import('firebase/firestore');
      const docRef = doc(db, 'notifications', notificationId);
      const docSnap = await getDoc(docRef);
      return {
        success: true,
        status: docSnap.exists() ? docSnap.data().deliveryStatus : 'unknown'
      };
    } catch (error) {
      console.error('Error verifying email delivery:', error);
      return { success: false, error: error.message };
    }
  }

  async sendPaymentReceiptEmail(paymentData) {
    try {
      const sendReceiptEmail = httpsCallable(functions, 'sendPaymentReceiptEmail');
      const result = await sendReceiptEmail({
        recipientEmail: paymentData.mpsEmail,
        recipientName: paymentData.mpsBusinessName,
        eventName: paymentData.eventName,
        amount: paymentData.totalAmount,
        platformFee: paymentData.platformFee,
        transactionId: paymentData.transactionId,
        paymentDate: new Date().toISOString(),
        receiptId: paymentData.receiptId
      });

      await this.logSentEmail({
        type: 'receipt',
        recipientEmail: paymentData.mpsEmail,
        recipientName: paymentData.mpsBusinessName,
        subject: `Payment Receipt - ${paymentData.eventName}`,
        relatedId: paymentData.receiptId,
        senderId: paymentData.eoUserId
      });

      return { success: true, result: result.data };
    } catch (error) {
      console.error('Error sending receipt email:', error);
      return { success: false, error: error.message };
    }
  }

  async sendApplicationApprovalEmail(applicationData) {
    try {
      const sendApprovalEmail = httpsCallable(functions, 'sendApplicationStatusEmail');
      const result = await sendApprovalEmail({
        type: 'approval',
        recipientEmail: applicationData.mpsEmail,
        recipientName: applicationData.businessName,
        eventName: applicationData.eventName,
        eventDate: applicationData.eventDate,
        registrationFee: applicationData.registrationFee,
        applicationId: applicationData.applicationId,
        organizerName: applicationData.organizerName
      });

      await this.logSentEmail({
        type: 'approval',
        recipientEmail: applicationData.mpsEmail,
        recipientName: applicationData.businessName,
        subject: `Application Approved - ${applicationData.eventName}`,
        relatedId: applicationData.applicationId,
        senderId: applicationData.eoUserId
      });

      return { success: true, result: result.data };
    } catch (error) {
      console.error('Error sending approval email:', error);
      return { success: false, error: error.message };
    }
  }

  async sendApplicationDenialEmail(applicationData) {
    try {
      const sendDenialEmail = httpsCallable(functions, 'sendApplicationStatusEmail');
      const result = await sendDenialEmail({
        type: 'denial',
        recipientEmail: applicationData.mpsEmail,
        recipientName: applicationData.businessName,
        eventName: applicationData.eventName,
        reason: applicationData.denialReason || '',
        applicationId: applicationData.applicationId,
        organizerName: applicationData.organizerName
      });

      await this.logSentEmail({
        type: 'denial',
        recipientEmail: applicationData.mpsEmail,
        recipientName: applicationData.businessName,
        subject: `Application Update - ${applicationData.eventName}`,
        relatedId: applicationData.applicationId,
        senderId: applicationData.eoUserId
      });

      return { success: true, result: result.data };
    } catch (error) {
      console.error('Error sending denial email:', error);
      return { success: false, error: error.message };
    }
  }

  async sendPaymentReminderEmail(reminderData) {
    try {
      const sendReminderEmail = httpsCallable(functions, 'sendPaymentReminderEmail');
      const result = await sendReminderEmail({
        recipientEmail: reminderData.mpsEmail,
        recipientName: reminderData.businessName,
        eventName: reminderData.eventName,
        amount: reminderData.registrationFee,
        dueDate: reminderData.dueDate,
        applicationId: reminderData.applicationId
      });

      await this.logSentEmail({
        type: 'payment_reminder',
        recipientEmail: reminderData.mpsEmail,
        recipientName: reminderData.businessName,
        subject: `Payment Reminder - ${reminderData.eventName}`,
        relatedId: reminderData.applicationId,
        senderId: reminderData.eoUserId
      });

      return { success: true, result: result.data };
    } catch (error) {
      console.error('Error sending payment reminder:', error);
      return { success: false, error: error.message };
    }
  }

  async sendDocumentRequestEmail(documentData) {
    try {
      const sendDocumentEmail = httpsCallable(functions, 'sendDocumentRequestEmail');
      const result = await sendDocumentEmail({
        recipientEmail: documentData.mpsEmail,
        recipientName: documentData.businessName,
        eventName: documentData.eventName,
        requiredDocuments: documentData.requiredDocuments,
        deadline: documentData.deadline,
        applicationId: documentData.applicationId
      });

      await this.logSentEmail({
        type: 'document_request',
        recipientEmail: documentData.mpsEmail,
        recipientName: documentData.businessName,
        subject: `Documents Required - ${documentData.eventName}`,
        relatedId: documentData.applicationId,
        senderId: documentData.eoUserId
      });

      return { success: true, result: result.data };
    } catch (error) {
      console.error('Error sending document request:', error);
      return { success: false, error: error.message };
    }
  }

  async sendBulkEmail(emailData) {
    try {
      const sendBulkEmail = httpsCallable(functions, 'sendBulkEmail');
      const result = await sendBulkEmail({
        recipients: emailData.recipients.map(r => ({
          email: r.email,
          name: r.businessName
        })),
        subject: emailData.subject,
        message: emailData.message,
        senderId: emailData.senderId,
        senderName: emailData.senderName
      });

      await this.logSentEmail({
        type: emailData.type || 'custom',
        recipientCount: emailData.recipients.length,
        recipients: emailData.recipients.map(r => ({ email: r.email, name: r.businessName })),
        subject: emailData.subject,
        message: emailData.message,
        senderId: emailData.senderId
      });

      return { success: true, result: result.data };
    } catch (error) {
      console.error('Error sending bulk email:', error);
      return { success: false, error: error.message };
    }
  }

  async sendNewApplicationNotification(applicationData) {
    try {
      const sendNotification = httpsCallable(functions, 'sendNewApplicationNotification');
      const result = await sendNotification({
        eoEmail: applicationData.eoEmail,
        eoName: applicationData.eoName,
        mpsBusinessName: applicationData.mpsBusinessName,
        eventName: applicationData.eventName,
        applicationId: applicationData.applicationId
      });

      return { success: true, result: result.data };
    } catch (error) {
      console.error('Error sending new application notification:', error);
      return { success: false, error: error.message };
    }
  }

  async sendApplicationApprovedSMS({ phoneNumber, vendorName, eventName, registrationFee }) {
    try {
      const fn = httpsCallable(functions, 'sendApplicationApprovedSMS');
      await fn({ phoneNumber, vendorName, eventName, registrationFee });
    } catch (err) {
      console.error('sendApplicationApprovedSMS error', err);
    }
  }

  async sendPaymentReminderSMS({ phoneNumber, vendorName, eventName, amount, dueDate }) {
    try {
      const fn = httpsCallable(functions, 'sendPaymentReminderSMS');
      await fn({ phoneNumber, vendorName, eventName, amount, dueDate });
    } catch (err) {
      console.error('sendPaymentReminderSMS error', err);
    }
  }

  async sendBoothAssignedSMS({ phoneNumber, vendorName, eventName, boothNumber, boothLocation }) {
    try {
      const fn = httpsCallable(functions, 'sendBoothAssignedSMS');
      await fn({ phoneNumber, vendorName, eventName, boothNumber, boothLocation });
    } catch (err) {
      console.error('sendBoothAssignedSMS error', err);
    }
  }

  async sendEventReminderSMS({ phoneNumber, vendorName, eventName, eventDate, boothNumber }) {
    try {
      const fn = httpsCallable(functions, 'sendEventReminderSMS');
      await fn({ phoneNumber, vendorName, eventName, eventDate, boothNumber });
    } catch (err) {
      console.error('sendEventReminderSMS error', err);
    }
  }

  async sendAnnouncementSMS({ recipients, message, eventName, senderName }) {
    try {
      const fn = httpsCallable(functions, 'sendAnnouncementSMS');
      await fn({ recipients, message, eventName, senderName });
    } catch (err) {
      console.error('sendAnnouncementSMS error', err);
    }
  }

  async sendVendorPaidNotificationEmail({ eoEmail, eoName, mpsBusinessName, eventName, amount, platformFee, transactionId }) {
    try {
      const fn = httpsCallable(functions, 'sendVendorPaidNotificationEmail');
      await fn({ eoEmail, eoName, mpsBusinessName, eventName, amount, platformFee, transactionId });
    } catch (err) {
      console.error('sendVendorPaidNotificationEmail error', err);
    }
  }

  async sendBoothAssignedEmail({ recipientEmail, vendorName, eventName, eventDate, boothNumber, boothLocation, organizerName }) {
    try {
      const fn = httpsCallable(functions, 'sendBoothAssignedEmail');
      await fn({ recipientEmail, vendorName, eventName, eventDate, boothNumber, boothLocation, organizerName });
    } catch (err) {
      console.error('sendBoothAssignedEmail error', err);
    }
  }

  async sendWaitlistAvailableEmail({ recipientEmail, buyerName, eventName, ticketTypeName, eventId }) {
    try {
      const fn = httpsCallable(functions, 'sendWaitlistAvailableEmail');
      await fn({ recipientEmail, buyerName, eventName, ticketTypeName, eventId });
    } catch (err) {
      console.error('sendWaitlistAvailableEmail error', err);
    }
  }

  async sendEventCancelledSMS({ recipients, eventName, reason, organizerName }) {
    try {
      const fn = httpsCallable(functions, 'sendEventCancelledSMS');
      await fn({ recipients, eventName, reason, organizerName });
    } catch (err) {
      console.error('sendEventCancelledSMS error', err);
    }
  }

  async logSentEmail(emailData) {
    try {
      await addDoc(this.sentEmailsRef, {
        ...emailData,
        status: 'sent',
        sentAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error logging sent email:', error);
    }
  }

  getDefaultSettings() {
    return {
      ticketSales: { enabled: true },
      lowTicketInventory: { enabled: true },
      registrationDeadlines: { enabled: true },
      payments: { enabled: true },
      refundRequests: { enabled: true },
      payoutStatus: { enabled: true },
      newSellerApplications: { enabled: true },
      sellerDocuments: { enabled: true },
      sellerPayments: { enabled: true },
      eventChanges: { enabled: true },
      attendeeCommunication: { enabled: true },
      emergencyAlerts: { enabled: true, required: true }
    };
  }
}

export const notificationService = new NotificationService();
